/**
 * Real North-America geography for the Command Center crown map.
 *
 * Renders US states + Canadian PROVINCES individually (SR pursues provinces on
 * their own), with Alaska and Hawaii drawn as insets (bottom-left) so they are
 * real, selectable, colorable regions. Everything is keyed to a 2-letter code
 * (USPS for states, province code for Canada). Each region carries a projected
 * path + centroid (for crown markers), computed once at module load.
 */
import { geoAlbers, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import statesTopo from 'us-atlas/states-10m.json'
import provincesFC from './canada-provinces.json'
import { PROVINCE_CODES } from '../../../lib/usStates.js'

export const W = 960
export const H = 600

const FIPS_TO_USPS = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA', '08': 'CO',
  '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL', '13': 'GA', '15': 'HI',
  '16': 'ID', '17': 'IL', '18': 'IN', '19': 'IA', '20': 'KS', '21': 'KY',
  '22': 'LA', '23': 'ME', '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN',
  '28': 'MS', '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND', '39': 'OH',
  '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI', '45': 'SC', '46': 'SD',
  '47': 'TN', '48': 'TX', '49': 'UT', '50': 'VT', '51': 'VA', '53': 'WA',
  '54': 'WV', '55': 'WI', '56': 'WY', '72': 'PR',
}

// US state features keyed by USPS.
const usStates = feature(statesTopo, statesTopo.objects.states).features
  .map((f) => {
    const code = FIPS_TO_USPS[String(f.id).padStart(2, '0')]
    return code ? { ...f, code, name: f.properties?.name || code } : null
  })
  .filter(Boolean)
const usByCode = Object.fromEntries(usStates.map((f) => [f.code, f]))
const provinces = provincesFC.features.map((f) => ({ ...f, code: f.properties.code, name: f.properties.name }))

// Territories are huge/far-north — draw them but don't let them dominate the fit.
const TERRITORIES = new Set(['YT', 'NT', 'NU'])
const AK = usByCode.AK
const HI = usByCode.HI

// Main projection: fit to lower-48 + southern provinces (AK/HI/territories excluded from fit).
const fitFeatures = [
  ...usStates.filter((f) => f.code !== 'AK' && f.code !== 'HI'),
  ...provinces.filter((p) => !TERRITORIES.has(p.code)),
]
const mainProj = geoAlbers()
  .rotate([96, 0])
  .parallels([29.5, 45.5])
  .fitExtent([[24, 18], [W - 24, H - 24]], { type: 'FeatureCollection', features: fitFeatures })
const mainPath = geoPath(mainProj)

// Inset projections for Alaska + Hawaii (own small boxes, bottom-left).
const akProj = AK && geoAlbers().rotate([154, 0]).parallels([55, 65]).fitExtent([[14, H - 168], [174, H - 20]], AK)
const hiProj = HI && geoAlbers().rotate([157, 0]).parallels([8, 18]).fitExtent([[182, H - 92], [262, H - 20]], HI)
const akPath = akProj && geoPath(akProj)
const hiPath = hiProj && geoPath(hiProj)

function region(f, path, inset = false) {
  const d = path(f)
  if (!d) return null
  const c = path.centroid(f)
  return { code: f.code, name: f.name, d, cx: c[0], cy: c[1], inset }
}

// All fillable/clickable regions.
export const REGIONS = [
  ...usStates.filter((f) => f.code !== 'AK' && f.code !== 'HI').map((f) => region(f, mainPath)),
  ...provinces.map((f) => region(f, mainPath)),
  AK ? region(AK, akPath, true) : null,
  HI ? region(HI, hiPath, true) : null,
].filter(Boolean)

export const REGION_BY_CODE = Object.fromEntries(REGIONS.map((r) => [r.code, r]))
export const IS_PROVINCE = (code) => PROVINCE_CODES.has(code)

// Inset label frames (for a subtle divider box around AK/HI).
export const INSETS = [
  AK ? { code: 'AK', label: 'Alaska', x: 14, y: H - 168, w: 160, h: 148 } : null,
  HI ? { code: 'HI', label: 'Hawaii', x: 182, y: H - 92, w: 80, h: 72 } : null,
].filter(Boolean)

/** Crown-marker pixel anchor for a code (state or province). */
export function anchorFor(code) {
  const r = REGION_BY_CODE[code]
  return r ? [r.cx, r.cy] : null
}
